import { Helmet } from "react-helmet-async";

const SITE = "https://loangorilla.com.au";
const DEFAULT_OG_IMAGE = `${SITE}/og-image.jpg`;

export interface SEOHeadProps {
  title: string;
  description: string;
  /** Path (e.g. "/car-loans") or full URL. Defaults to undefined → no canonical emitted. */
  canonical?: string;
  /** Path or full URL of OG image. Falls back to site default. */
  ogImage?: string;
  /** og:type override. Defaults to "website". Use "article" for guides/blog posts. */
  ogType?: "website" | "article";
  /** Emit robots noindex, nofollow. */
  noIndex?: boolean;
  /** Self-referencing hreflang URL. Defaults to canonical. Pass current page URL when canonical points elsewhere (e.g. alias pages). */
  hreflangUrl?: string;
  /** One or more JSON-LD schema objects to inject. */
  schemaJson?: Record<string, unknown> | Record<string, unknown>[];
}

function toAbsolute(value: string): string {
  if (/^https?:\/\//i.test(value)) return value;
  return `${SITE}${value.startsWith("/") ? value : `/${value}`}`;
}

/**
 * SEOHead — central per-page meta. Render once at the top of every page
 * component (above <Layout> / <main>). Helmet dedupes by tag name + key
 * attribute, and the deepest-rendered instance wins, so a page-level
 * SEOHead always overrides app-level defaults.
 */
export function SEOHead({
  title,
  description,
  canonical,
  ogImage,
  ogType = "website",
  noIndex = false,
  hreflangUrl,
  schemaJson,
}: SEOHeadProps) {
  const url = canonical ? toAbsolute(canonical) : undefined;
  const hrefUrl = hreflangUrl ? toAbsolute(hreflangUrl) : url;
  const img = toAbsolute(ogImage ?? DEFAULT_OG_IMAGE);
  const schemas = Array.isArray(schemaJson)
    ? schemaJson
    : schemaJson
    ? [schemaJson]
    : [];

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      {noIndex && <meta name="robots" content="noindex, follow" />}
      {url && <link rel="canonical" href={url} />}
      {hrefUrl && <link rel="alternate" hrefLang="en-AU" href={hrefUrl} />}
      {hrefUrl && <link rel="alternate" hrefLang="x-default" href={hrefUrl} />}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      {url && <meta property="og:url" content={url} />}
      <meta property="og:image" content={img} />
      <meta property="og:site_name" content="LoanGorilla" />
      <meta property="og:locale" content="en_AU" />
      <meta property="og:image:width" content="1024" />
      <meta property="og:image:height" content="813" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={img} />
      {schemas.map((s, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(s)}</script>
      ))}
    </Helmet>
  );
}

export default SEOHead;